import { first, capitalize } from "lodash"
import { Fragment, useMemo, useState } from "react"

import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/reusable/Table"
import { useVariantsByIds } from "~/hooks/useVariants"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { formatDate, getStartOfCurrentWeekUTC, isBefore, isBetween } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"

import { ShipmentMethodIcon } from "../../icons"
import { Alert } from "../../reusable/Alert"
import { HoverCardHeader, HoverCardTitle } from "../../reusable/hovercard/HoverCard"
import { Tooltip } from "../../reusable/Tooltip"
import { CaretDown } from "@phosphor-icons/react"
import { getTailwindValue } from "~/utils/tailwind"
import { sumBy, groupBy } from "~/utils/tsUtils"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { IncomingOrdersStatusBadge } from "./IncomingOrdersStatusBadge"
import { calculateIncomingStockStatus } from "~/utils/incomingOrdersUtils"

type IncomingStockCardProps = {
  variantIds: string[]
  warehouseId?: string
  dateRange?: { start: Date; end: Date }
  skuIds?: string[]
  includeArrived?: boolean
  statusFilter?: "incoming" | "arrived"
}

export const IncomingStockCard = ({
  variantIds,
  warehouseId,
  dateRange,
  skuIds: skuIdsOverride,
  includeArrived = false,
  statusFilter,
}: IncomingStockCardProps) => {
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const { data: warehouses } = api.warehouse.get.useQuery()
  const [detailsOpen, setDetailsOpen] = useState<{ [key: string]: boolean }>({})
  const variants = useVariantsByIds(variantIds)
  const skuIds = useMemo(
    () => skuIdsOverride ?? variants.flatMap((v) => v.skuIds),
    [skuIdsOverride, variants],
  )
  const showWarehouse = !!warehouses && warehouses?.length > 1
  const { data: skus } = api.sku.getSkuInfo.useQuery({ skuIds })
  // Only set includeArrived when true to keep the query key shape identical to other
  // {skuIds}-only callers (ClosingStockHoverCard, SuggestedOrderTile) and reuse their cache.
  const { data: incomingOrdersMap, isLoading: loadingIncomingOrders } =
    api.sku.incomingOrders.useQuery(includeArrived ? { skuIds, includeArrived: true } : { skuIds })
  const isMultiProduct = useMemo(
    () => skus && new Set(Array.from(skus.values())?.map((sku) => sku.productId)).size > 1,
    [skus],
  )
  const isMultiVariant = useMemo(
    () => skus && new Set(Array.from(skus.values())?.map((sku) => sku.variantId)).size > 1,
    [skus],
  )

  const incomingOrders = useMemo(() => {
    if (!incomingOrdersMap) return []
    return Array.from(incomingOrdersMap?.values()).flat()
  }, [incomingOrdersMap])

  if (loadingIncomingOrders) return <LoadingOverlay />

  const effectiveWarehouseId = warehouseId ?? selectedWarehouseId
  const filteredIncomingStock = incomingOrders
    .filter((x) => (statusFilter ? x.status === statusFilter : true))
    .filter((x) => {
      if (dateRange === undefined) return true
      if (isBetween(x.expectedArrivalDate, dateRange.start, dateRange.end)) return true
      // Include delayed orders (expected before current week) in the current period,
      // mirroring how the IncomingStock row sums them into the current week/month.
      if (
        statusFilter === "incoming" &&
        isBetween(getStartOfCurrentWeekUTC(), dateRange.start, dateRange.end) &&
        isBefore(x.expectedArrivalDate, dateRange.start)
      ) {
        return true
      }
      return false
    })
    .sort((a, b) => {
      const currentWeekStart = getStartOfCurrentWeekUTC()
      const aDelayed = a.status !== "arrived" && isBefore(a.expectedArrivalDate, currentWeekStart)
      const bDelayed = b.status !== "arrived" && isBefore(b.expectedArrivalDate, currentWeekStart)
      if (aDelayed !== bDelayed) return aDelayed ? 1 : -1
      return a.expectedArrivalDate.getTime() - b.expectedArrivalDate.getTime()
    })
    .filter((x) => (effectiveWarehouseId ? x.warehouseId === effectiveWarehouseId : true))
    .map((x) => ({
      ...skus?.get(x.skuId),
      ...x,
    }))

  const showShipmentMethod = incomingOrders?.some((x) => x.shipmentMethod)
  const delayedIncomingStock = filteredIncomingStock?.filter(
    (x) => x.status !== "arrived" && isBefore(x.expectedArrivalDate, getStartOfCurrentWeekUTC()),
  )

  const cardTitle = statusFilter === "arrived" ? "Arrived stock" : "Incoming stock"

  if (filteredIncomingStock.length === 0)
    return (
      <div>
        <div className="p-5 text-base font-medium">{cardTitle}</div>
        <div className="p-5 pt-0">
          {statusFilter === "arrived" ? "No arrived stock..." : "No incoming stock..."}
        </div>
      </div>
    )

  // Split partially-arrived production orders into separate rows per status (matches
  // IncomingOrdersTable's row model) so the status badge can describe the whole group.
  const groupedFilteredIncomingStock = groupBy(
    filteredIncomingStock,
    (o) => `${o.productionOrderId}-${o.warehouseId}-${o.status}`,
  )

  return (
    <div className="text-left">
      {!!delayedIncomingStock?.length && (
        <div className="px-4 pt-3 w-[500px]">
          <Alert variant="warning">
            <p>You have incoming orders that seem to be delayed</p>
            <br />
            <p>
              Please update the incoming orders if the stock hasn&apos;t arrived. Since this might
              affect the calculated stockout times.
            </p>
          </Alert>
        </div>
      )}
      <HoverCardHeader>
        <HoverCardTitle>{cardTitle}</HoverCardTitle>
      </HoverCardHeader>
      <div className="max-h-[300px] overflow-y-auto">
        <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="text-left text-xs">
              <TableHead>Order date</TableHead>
              <TableHead>Expected arrival date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Quantity</TableHead>
              {showWarehouse ? <TableHead>Warehouse</TableHead> : null}
              <TableHead>Prod. order id</TableHead>
              {showShipmentMethod && <TableHead>Shipment method</TableHead>}
              {skuIds.length > 1 && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody className="text-xs">
            {Object.entries(groupedFilteredIncomingStock).map(([key, orders]) => {
              const firstOrder = first(orders)
              const productionOrderId = firstOrder?.productionOrderId ?? ""
              const warehouseId = firstOrder?.warehouseId ?? ""
              const orderDate = firstOrder?.orderDate
              const expectedArrivalDate = firstOrder?.expectedArrivalDate
              const quantity = sumBy(orders, "quantity") ?? 0
              const shipmentMethod = firstOrder?.shipmentMethod ?? ""
              const status = firstOrder ? calculateIncomingStockStatus(firstOrder) : null

              return (
                <Fragment key={key}>
                  <TableRow
                    onClick={() =>
                      setDetailsOpen({
                        ...detailsOpen,
                        [key]: !detailsOpen[key],
                      })
                    }
                  >
                    <TableCell>{formatDate(orderDate)}</TableCell>
                    <TableCell>{formatDate(expectedArrivalDate)}</TableCell>
                    <TableCell>
                      {status && <IncomingOrdersStatusBadge status={status} size="sm" />}
                    </TableCell>
                    <TableCell>{formatNumber(quantity, 0)} units</TableCell>
                    {showWarehouse && (
                      <TableCell>
                        {warehouses?.find((w) => w.id === warehouseId)?.name ?? warehouseId}
                      </TableCell>
                    )}
                    <TableCell>{productionOrderId}</TableCell>
                    {showShipmentMethod && (
                      <TableCell>
                        {shipmentMethod && (
                          <div className="flex flex-row gap-2 items-center justify-end">
                            <ShipmentMethodIcon
                              color={getTailwindValue("--color-fg-subtle")}
                              shipmentMethod={shipmentMethod.toLowerCase()}
                            />
                            {capitalize(shipmentMethod)}
                          </div>
                        )}
                      </TableCell>
                    )}
                    {skuIds.length > 1 && (
                      <TableCell>
                        <Tooltip title="Details for sizes">
                          <CaretDown
                            size="16"
                            className="data-[open=true]:rotate-90 transition-transform cursor-pointer"
                            data-open={detailsOpen[key]}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  {detailsOpen[key] && (
                    <>
                      {orders
                        ?.sort((a, b) => {
                          // Weighing productName and variantName higher to sort by product, then variant, then size - this is for the "All" variant case
                          const sizeSort =
                            a.size && b.size ? sortSizesComparator(a.size, b.size) : 0
                          const variantSort =
                            a.variantName && b.variantName
                              ? a.variantName.localeCompare(b.variantName) * 100
                              : 0
                          const productSort =
                            a.productName && b.productName
                              ? a.productName.localeCompare(b.productName) * 1000
                              : 0
                          return productSort + variantSort + sizeSort
                        })
                        .map((order) => {
                          return (
                            <TableRow key={order.skuId} className="bg-subtle [&>td]:py-1 text-xs">
                              <TableCell colSpan={3} className="text-left">
                                {isMultiProduct ? `${order?.productName} - ` : ""}{" "}
                                {isMultiVariant ? `${order?.variantName} - ` : ""} {order?.size}
                              </TableCell>
                              <TableCell className="text-xs">
                                {formatNumber(order.quantity, 0)} units
                              </TableCell>
                              <TableCell colSpan={4}></TableCell>
                            </TableRow>
                          )
                        })}
                    </>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
